import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import useDetectPrint from 'react-detect-print';

import Adhesion from '../../../atoms/adhesion';
import CardResource from '../../../molecules/profile/card-resource';
import CardResourceContainer from '../../../molecules/profile/card-resource-container';
import ContractFees from '../../../molecules/profile/contract-fees';
import ContractholderWithdrawlsText from '../../../../helpers/statics/contractholder-withdrawls-text';
import FooterDisclosures from '../../../atoms/footer-disclosures';
import HeaderQuoteBar from '../../../molecules/profile/header-quote-bar';
import HistoricalRatesOfReturn from '../../../molecules/profile/historical-rates-of-return';
import InvestmentFacts from '../../../molecules/profile/investment-facts';
import InvestmentStrategy from '../../../molecules/profile/investment-strategy';
import PageHeader from '../../../molecules/profile/page-header';
import PageMain from '../../../molecules/profile/page-main';
import PageSideRail, { DisclosurePrint } from '../../../molecules/profile/page-side-rail';
import SectionDivider from '../../../atoms/section-divider';
import TextModule from '../../../molecules/profile/text-module';
import contractFeesText from '../../../../helpers/statics/contract-fees-text';
import interestRatesText from '../../../../helpers/statics/interest-rates-text';
import participantWithdrawalsText from '../../../../helpers/statics/participant-withdrawals-text';
import sectionDividerOptions from '../../../../helpers/statics/section-divider-options';
import transformInvestmentFacts from '../../../../stores/profile/modules/transform-investment-facts';
import useTiaaStableValueStore from '../../../../stores/profile/use-fixed-annuity-tiaa-stable-value-store';
import { className } from '../../../../utils/GlobalCssProxy';
import { getIsDynamicGreetingShown } from '../../../molecules/profile/dynamic-greeting/dynamic-greeting-helper';

const StableValue = () => {
  const store = useTiaaStableValueStore();
  const quoteData = store.quote.isLoaded ? store.quote.data : {};
  const fundName =
    quoteData && quoteData.fundName ? `${quoteData.fundType}: ${quoteData.fundName}` : undefined;
  const interestRateData = store.tsvInterestRate.isLoaded ? store.tsvInterestRate.data : {};
  const participantHoldingsData =
    store.participantHoldings && store.participantHoldings.isLoaded
      ? store.participantHoldings.data
      : {};
  const showDynamicGreeting =
    participantHoldingsData && getIsDynamicGreetingShown(participantHoldingsData);
  const disclosureList = store.disclosureList.isLoaded ? store.disclosureList.data : [];

  const printing = useDetectPrint();
  return (
    <div data-template="FixedAnnuityTIAAStableValue">
      <PageHeader
        fundName={fundName}
        displayAsFundType="Stable Value"
        sectionNavOptions={[
          sectionDividerOptions.performance,
          sectionDividerOptions.characteristics,
          sectionDividerOptions.resources
        ]}
      >
        <HeaderQuoteBar>
          <HeaderQuoteBar.DeclaredCreditingRate
            value={quoteData.declaredCreditingRate}
            asOfDate={quoteData.tiaaAsOfDate}
          />
          <HeaderQuoteBar.NetCreditingRate
            value={quoteData.netCreditingRate}
            responsive={printing ? undefined : 1}
          />
          <HeaderQuoteBar.TotalContractFee
            value={quoteData.totalContractFee}
            responsive={printing ? undefined : 2}
          />
          <HeaderQuoteBar.AccountInceptionDate
            asOfDate={quoteData.inceptionDate}
            responsive={printing ? undefined : 3}
          />
        </HeaderQuoteBar>
      </PageHeader>
      <PageMain>
        <SectionDivider
          labelText={sectionDividerOptions.performance.label}
          id={sectionDividerOptions.performance.id}
          isDynamicGreetingShown={showDynamicGreeting}
          dynamicGreetingParticipantHoldings={participantHoldingsData}
        >
          <Row>
            <Col>
              <HistoricalRatesOfReturn />
            </Col>
          </Row>
          <Row>
            <Col>
              <ContractFees
                heading="Contract Fees"
                feeType={store.contractFees.isLoaded ? store.contractFees.data : {}}
                dateOptions={{ formatString: 'MMM YYYY' }}
              />
            </Col>
            <Col md={8}>
              <Adhesion.Performance />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.characteristics.label}
          id={sectionDividerOptions.characteristics.id}
        >
          <Row>
            <Col md={printing ? undefined : 6}>
              <TextModule
                analyticsId="InterestRates"
                moduleHeader="Interest Rates"
                textHighlighted={interestRatesText.headline}
                textNormal={interestRatesText.body}
                modalText={interestRatesText.modalText(
                  interestRateData.priceDate,
                  interestRateData.guaranteedRate
                )}
              />
            </Col>
            <Col md={printing ? undefined : 6}>
              <TextModule
                analyticsId="ContractFees"
                moduleHeader="Contract Fees"
                textHighlighted={contractFeesText.headline}
                textNormal={contractFeesText.body}
              />
            </Col>
          </Row>
          <Row>
            <Col md={printing ? undefined : 6}>
              <TextModule
                analyticsId="ParticipantWithdrawals"
                moduleHeader="Participant Withdrawals, Transfers and Lifetime Income Options"
                textHighlighted={participantWithdrawalsText.headline}
                textNormal={participantWithdrawalsText.body}
                modalText={participantWithdrawalsText.modalText}
              />
            </Col>
            <Col md={printing ? undefined : 6}>
              <TextModule
                analyticsId="ContractHolderWithdrawals"
                moduleHeader="Contractholder Withdrawals"
                textHighlighted={ContractholderWithdrawlsText.headline}
                textNormal={ContractholderWithdrawlsText.body}
                modalText={ContractholderWithdrawlsText.modalText}
              />
            </Col>
          </Row>
          <Row>
            <Col md={printing ? 6 : 4}>
              <InvestmentStrategy
                heading="Investment Description"
                {...(store.investmentStrategy.data || {})}
              >
                {store.investmentStrategy.isLoaded
                  ? store.investmentStrategy.data.investmentStrategy
                  : ''}
              </InvestmentStrategy>
            </Col>
            <Col md={printing ? 0 : 4}>
              <Adhesion.Characteristics />
            </Col>
            <Col md={printing ? 6 : 4}>
              <InvestmentFacts
                header="Investment Facts"
                headerAsOfDate={
                  store.investmentFacts.isLoaded && store.investmentFacts.data.tsvAsOfDate
                    ? store.investmentFacts.data.tsvAsOfDate
                    : ''
                }
                facts={
                  store.investmentFacts.isLoaded
                    ? transformInvestmentFacts(
                      store.investmentFacts.data,
                      store.fundType,
                      store.assetClass
                    )
                    : [] // prettier-ignore
                }
              ></InvestmentFacts>
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.resources.label}
          id={sectionDividerOptions.resources.id}
        >
          <CardResourceContainer>
            <CardResource.SimpleLink
              bodyText="TIAA Stable Value"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Learn"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/retire/financial-products/annuities/retirement-plan-annuities/tiaa-stable-value"
              iconVariant="Link"
              titleText="LEARN"
            />
            <CardResource.SimpleLink
              bodyText="Lifetime Income Calculator"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Tools"
              data-analytics-section="component"
              href="https://shared.tiaa.org/public/publictools/targetincome/annuityEstimator"
              iconVariant="Link"
              titleText="TOOLS"
            />
            <CardResource.SimpleLink
              bodyText="Retirement Planner"
              href="https://shared.tiaa.org/private/digitalinvestadvice/experience/personalgoals/userlanding"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Planning"
              data-analytics-section="component"
              iconVariant="Link"
              titleText="PLANNING"
            />
            <CardResource.SimpleLink
              bodyText="Investment Performance"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Research"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/investment-performance"
              iconVariant="Link"
              titleText="RESEARCH"
            />
          </CardResourceContainer>
        </SectionDivider>
        <FooterDisclosures />
        <div {...className('admaster-number', 'd-print-none')}>X-3240757P-Q0923W</div>
        <DisclosurePrint disclosureList={disclosureList} admasterNumber="X-3240757P-Q0923W" />
      </PageMain>
      <PageSideRail />
    </div>
  );
};

export default StableValue;
